import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject,  } from 'rxjs';
import { takeUntil,  } from 'rxjs/operators';

import { IGetCodes } from 'src/app/interfaces/codes';
import {
  ContentManagementService,
  ICourseMainTopic,
  // If your service exports the payload type, import it and use it instead of `any`
  // InsertOrUpdateCourseMainTopicPayload
} from 'src/app/Services/content-management/content-management.service';

@Component({
  selector: 'app-course-main-topic-add-edit',
  templateUrl: './course-main-topic-add-edit.component.html',
  styleUrls: ['./course-main-topic-add-edit.component.css']
})
export class CourseMainTopicAddEditComponent implements OnInit, OnDestroy {
  form!: FormGroup;
  courseMainTopic: ICourseMainTopic | null = null;

  @Input() item: {
    courseMainTopicId?: number;
    courseMainTopicName?: string;
    courseName?: string;
    ExamAgencyName?: string;
    examYear?: string;
    courseMainTopicStatusTypeCode?: string;
    displayOrder?: number;
    subjectId?: number;
    subjectName?: string;
    majorTopicId?: number;
    majorTopicName?: string;
    courseMainTopicSubjectId?: string;
    examAgencyId?: number;
    courseId?: number;
    examShortName?: string;
  } = {};

  @Input() Screen = '';
  @Input() dataFromStudyUnit: any = null;
  @Input() courseId: string | null = null;

  mainTopicstatusTypeCode: IGetCodes[] = [];

  params: {
    c_id?: string;
    eA_Name?: string;
    c_Name?: string;
    eY_Name?: string;
    eA_Id?: string;
    cM_id?: string;
    cC_Code?: string;
    cC_Name?: string;
  } = {};

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    public activeModal: NgbActiveModal,
    public service: ContentManagementService,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.buildForm();
    this.getCourseMainTopicStatusTypeCode();

    // If opened from STUDYUNIT flow, load display order and params
    if (this.Screen === 'STUDYUNIT') {
      this.getMainCourseMainTopic();
      this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((p: any) => {
        this.params = p || {};
      });
    }
  }

  private buildForm(): void {
    // Keep form controls names consistent with template (status, courseMainTopic etc.)
    this.form = this.fb.group({
      examAgency: [{ value: this.item?.ExamAgencyName ?? null, disabled: true }],
      course: [{ value: this.item?.courseName ?? null, disabled: true }],
      year: [{ value: this.item?.examYear ?? null, disabled: true }],
      courseMainTopic: [this.item?.courseMainTopicName ?? ''],
      status: [this.item?.courseMainTopicStatusTypeCode ?? null],
      displayOrder: [{ value: this.item?.displayOrder ?? 0, disabled: true }]
    });
  }

  private getMainCourseMainTopic(): void {
    if (!this.dataFromStudyUnit) return;

    const payload = {
      SubjectID: this.dataFromStudyUnit.Parent_Id,
      CourseID: this.courseId ? +this.courseId : null,
      CourseMainTopicID: null,
      CourseMainTopicStatusTypeCode: null,
      MajorTopicID: this.dataFromStudyUnit.ID
    };

    this.service.getCourseMainTopicDisplayOrder(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: ICourseMainTopic[] | any) => {
          if (!res) return;
          // find by Major_Topic_ID (matches API naming)
          const found = (res as any[]).filter((w)=>w.Deleted_Ind!=='Y').find((e: any) => e.Major_Topic_ID === this.dataFromStudyUnit.ID);
          if (found) {
            this.courseMainTopic = found as ICourseMainTopic;
            // patch form with fallback to query params if available
            this.form.patchValue({
              examAgency: this.courseMainTopic?.Exam_agency_Name ?? this.params?.eA_Name ?? null,
              course: this.courseMainTopic?.Course_Name ?? this.params?.c_Name ?? null,
              year: this.courseMainTopic?.Exam_Year ?? this.params?.eY_Name ?? null,
              courseMainTopic: this.courseMainTopic?.Course_Main_Topic_Name ?? '',
              status: this.courseMainTopic?.Course_Main_Topic_Status_Type_Code ?? null,
              displayOrder: this.courseMainTopic?.Display_Order ?? 0
            });
          }else{
            if(this.params){
            this.form.patchValue({
              examAgency: this.params?.eA_Name ?? null,
              course: this.params?.c_Name ?? null,
              year:  this.params?.eY_Name ?? null,
              displayOrder: this.courseMainTopic?.Display_Order ?? 0
            });
          }
          }
        },
        error: (err) => {
          console.error('getCourseMainTopicDisplayOrder error:', err);
        }
      });
  }

  getCourseMainTopicStatusTypeCode(): void {
    this.service.getCourseMainStatusType()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: IGetCodes[] | any) => {
          this.mainTopicstatusTypeCode = Array.isArray(res) ? res : [];
        },
        error: (err) => {
          console.error('getCourseMainStatusType error:', err);
          this.mainTopicstatusTypeCode = [];
        }
      });
  }

  save(): void {
    // unify form snapshot:
    const formValue = this.form.getRawValue();

    // Shared payload builder
    const buildPayload = (opts: {
      CourseMainTopicID: number | null | undefined;
      SubjectID: number | null | undefined;
      CourseID: number | null | undefined;
      DisplayOrder: number | null | undefined;
      DeletedInd:string
    }) => {
      // IMPORTANT: API requires LoggedInUser (see earlier error). Replace getLoggedInUser() implementation with your auth.
      const payload: any = {
        CourseMainTopicID: opts.CourseMainTopicID ?? null,
        SubjectID: opts.SubjectID ?? null,
        CourseID: opts.CourseID ?? null,
        CourseMainTopicName: formValue.courseMainTopic ?? '',
        CourseMainTopicStatusTypeCode: formValue.status ?? null,
        DisplayOrder: opts.DisplayOrder ?? 0,
        NewCourseMainTopicID: null,
        DeletedInd:'N'
      };
      return payload;
    };

    if (this.Screen === 'MAIN_TOPIC_DISPLAY_ORDER') {
      const payload = buildPayload({
        CourseMainTopicID: this.item?.courseMainTopicId,
        SubjectID: this.item?.majorTopicId,
        CourseID: this.item?.courseId,
        DisplayOrder: this.item?.displayOrder,
        DeletedInd:'N'
      });

      this.service.InsertOrUpdateCourseMainTopic(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
            console.log(res,
              'res1'
            );

            this.activeModal.close({ result: res, payload });
          },
          error: (err) => {
            console.error('InsertOrUpdateCourseMainTopic error:', err);
          }
        });

    } else if (this.Screen === 'STUDYUNIT') {
      if (this.courseMainTopic) {


      const payload = buildPayload({
        CourseMainTopicID: this.courseMainTopic?.Course_Main_Topic_Id,
        SubjectID: this.courseMainTopic?.Major_Topic_ID,
        CourseID: this.courseMainTopic?.Course_ID,
        DisplayOrder: this.courseMainTopic?.Display_Order,
        DeletedInd:'N'
      });

      this.service.InsertOrUpdateCourseMainTopic(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
          console.log(res,'res2');
          this.activeModal.close({ result: res, payload });
          },
          error: (err) => {
            console.error('InsertOrUpdateCourseMainTopic error:', err);
          }
        });
      }
      else{

      const payload = buildPayload({
        CourseMainTopicID: null,
        SubjectID: this.dataFromStudyUnit?.ID,
        CourseID: this.courseId ? +this.courseId:null,
        DisplayOrder: 0,
        DeletedInd:'N'
      });
              this.service.InsertOrUpdateCourseMainTopic(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
                                    console.log(res,
              'res3'
            );
            this.activeModal.close({ result: res, payload });

          },
          error: (err) => {
            console.error('InsertOrUpdateCourseMainTopic error:', err);
          }
        });
      }
    } else {
      // fallback: try to save using available input values
      const payload = buildPayload({
        CourseMainTopicID: this.item?.courseMainTopicId ?? null,
        SubjectID: this.item?.subjectId ?? null,
        CourseID: this.item?.courseId ?? null,
        DisplayOrder: this.item?.displayOrder ?? 0,
        DeletedInd:'N'
      });

      this.service.InsertOrUpdateCourseMainTopic(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => this.activeModal.close({ result: res, payload }),
          error: (err) => console.error('InsertOrUpdateCourseMainTopic error:', err)
        });
    }
  }

  cancel(): void {
    this.activeModal.dismiss();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }


}
